import { openLoginModal } from './loginModal.mjs';
import { getLocally, storeLocally, removeLocally, showSpinner, hideSpinner } from './utils.mjs';
import { getAvatarColor } from './components.mjs';

/**
 * Entry point bundled separately for the marketing landing page (see
 * build-landing.mjs) — wires up the header avatar/login control to open the
 * exact same login/account modals the app uses, so the two never duplicate
 * this logic. Landing pages don't have per-page IDs to hook, so this binds
 * by attribute/id across however many matching elements exist on the page.
 *
 * Firebase's auth SDK is ~110KB, easily the biggest thing in this bundle —
 * and the overwhelming majority of landing-page visitors never touch auth
 * at all. So it's not loaded up front: clicking the login control is the
 * only thing that pulls it in for a fresh visitor. The one exception is a
 * visitor who previously logged in *on this same origin* (landing and the
 * app are separate origins per Terraform — this can't see an app-side
 * session, only a landing-side one) — CACHED_EMAIL_KEY, written whenever
 * onAuthStateChanged confirms a real login, lets that return visit show the
 * avatar immediately instead of flashing the logged-out hamburger, at the
 * cost of eagerly loading Firebase for just that subset to reconcile it.
 *
 * When logged in, the avatar fully replaces the hamburger/nav login link
 * (rather than sitting alongside it) — clicking it opens the account modal
 * directly. This does mean a logged-in mobile visitor loses the hamburger's
 * Home/About/Blog/FAQ/Contact links; those remain reachable via the footer.
 *
 * A freshly-created email/password account that hasn't clicked its
 * verification link yet has a valid Firebase session (Firebase authenticates
 * on signup regardless of verification — that's normal, not a bug), but
 * isn't treated as "logged in" here — same as the app's own route gate
 * (main.mjs) and the Flutter app's VerifyEmailScreen. The header stays in
 * its logged-out state; clicking it still opens the account modal (since
 * `auth.currentUser` is truthy), which shows the "verify your email" prompt
 * instead of the login form.
 */

import { needsVerification, watchAuthState, resyncUserData, pollUntilVerified } from './authWatcher.mjs';
import t from 'virtual:i18n';

const CACHED_EMAIL_KEY = 'splitpal_cached_account_email';

document.addEventListener('DOMContentLoaded', () => {
    // Legacy cutover: guests arriving from the old app.splitpal.io "moved"
    // banner carry their old group ids in ?legacy=. Import them into the
    // shared splitpal:groups cache and refresh the
    // recent-groups widget. Fire-and-forget - it never blocks page render.
    // Ads land HERE (splitpal.io/?ref=<campaign>), not on the /tabs SPA - and
    // the login handoff strips the query string. Capture the first-touch origin
    // on the landing page; the shared pending/ack handshake delivers it now or,
    // if this page navigates away first, from the SPA's own capture call.
    import('./attribution.mjs')
        .then(({ captureAttribution }) => captureAttribution())
        .catch((e) => console.warn('attribution capture failed to load:', e?.message));

    if (new URLSearchParams(window.location.search).has('legacy')) {
        import('./legacyMigration.mjs')
            .then(({ checkAndRunLegacyMigration }) => checkAndRunLegacyMigration())
            .then(() => document.dispatchEvent(new CustomEvent('splitpal:groups-updated')))
            .catch((e) => console.error('Legacy import on landing failed:', e));
    }

    const loginEls = document.querySelectorAll('[data-splitpal-login]');
    const avatarEl = document.getElementById('header-avatar-slot');
    const hamburger = document.querySelector('.hamburger');
    const mobileMenu = document.querySelector('.mobile-menu');
    if (loginEls.length === 0 && !avatarEl) return;

    const applyAvatar = (email, isAnonymous, photoURL) => {
        if (!avatarEl) return;
        avatarEl.style.backgroundImage = photoURL ? `url(${photoURL})` : 'none';
        avatarEl.style.backgroundColor = photoURL ? 'transparent' : getAvatarColor(email);
        avatarEl.textContent = photoURL ? '' : (isAnonymous ? 'G' : email.charAt(0).toUpperCase());
        avatarEl.classList.add('visible');
    };

    const showLoggedOutUI = () => {
        if (avatarEl) {
            avatarEl.classList.remove('visible');
            avatarEl.style.backgroundImage = '';
            avatarEl.textContent = '';
        }
        document.body.classList.remove('logged-in');
        if (hamburger) {
            hamburger.style.display = '';
            hamburger.classList.remove('active');
        }
        if (mobileMenu) mobileMenu.classList.remove('active');
        loginEls.forEach((el) => {
            (el.closest('li') || el).style.display = '';
            el.textContent = 'Login';
        });
    };

    const showLoggedInUI = async (user) => {
        const email = user.email || 'guest@splitpal.io';
        applyAvatar(email, user.isAnonymous, user.photoURL);

        document.body.classList.add('logged-in');
        if (hamburger) {
            hamburger.style.display = '';
        }
        loginEls.forEach((el) => {
            (el.closest('li') || el).style.display = '';
            el.textContent = 'Logout';
        });
    };

    // A freshly-created, unverified account has no dedicated screen here (unlike
    // the app's verifyEmail.mjs) — the header just looks logged-out. But nothing
    // else would ever notice the visitor clicking the emailed link in another
    // tab (Firebase doesn't refire onAuthStateChanged just because emailVerified
    // flips server-side), so this polls until it does, then re-runs the exact
    // same "just signed in" handling as a real sign-in would.
    let stopVerificationPoll = null;

    // Persistent pending-verification banner. The header deliberately looks
    // logged-out while verification is pending (see comment above) — which
    // means that once the login modal's waiting room is closed, NOTHING on
    // the page explains why signed-up features don't work. A visitor in that
    // state concludes the product is broken and starts recreating their
    // groups (the tramlovesyou incident, 2026-08-13). This banner is the
    // always-visible signal, with its own resend control.
    const VERIFY_BANNER_ID = 'splitpal-verify-banner';
    const hideVerifyBanner = () => {
        const el = document.getElementById(VERIFY_BANNER_ID);
        if (el) el.remove();
    };
    const showVerifyBanner = (email) => {
        if (document.getElementById(VERIFY_BANNER_ID)) return;
        const bar = document.createElement('div');
        bar.id = VERIFY_BANNER_ID;
        Object.assign(bar.style, {
            position: 'fixed',
            top: '0',
            left: '0',
            right: '0',
            zIndex: '10000',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: '10px',
            padding: '10px 16px',
            backgroundColor: '#fff8e1',
            borderBottom: '1px solid #ecd9a0',
            fontSize: '14px',
            color: '#5d4a1a',
            textAlign: 'center'
        });
        const text = document.createElement('span');
        const template = t.verificationLinkSentTo || "We've sent a verification link to your email address:\n{email}";
        text.textContent = `📧 ${template.replace('{email}', email || '').replace(/\n/g, ' ').trim()}`;
        const resend = document.createElement('button');
        Object.assign(resend.style, {
            background: 'none',
            border: '1px solid #c9b06a',
            borderRadius: '100px',
            padding: '4px 14px',
            fontSize: '13px',
            fontWeight: '600',
            color: '#5d4a1a',
            cursor: 'pointer'
        });
        resend.textContent = t.resendEmail || 'Resend email';
        resend.addEventListener('click', async () => {
            resend.disabled = true;
            resend.style.opacity = '0.5';
            try {
                const { sendVerificationEmail } = await import('./firebase.mjs');
                await sendVerificationEmail();
                resend.textContent = '✓ ' + (t.sent || 'Sent');
            } catch (err) {
                console.error('Failed to resend verification email:', err);
                resend.disabled = false;
                resend.style.opacity = '1';
            }
        });
        bar.appendChild(text);
        bar.appendChild(resend);
        document.body.appendChild(bar);
    };

    const handleAuthUser = async (user, isSettledTransition) => {
        if (user && !needsVerification(user)) {
            if (stopVerificationPoll) {
                stopVerificationPoll();
                stopVerificationPoll = null;
            }
            hideVerifyBanner();
            showLoggedInUI(user);
            storeLocally(CACHED_EMAIL_KEY, user.email || '');
            storeLocally('splitpal_cached_account_label', user.email || 'Guest');

            if (isSettledTransition) {
                await resyncUserData({
                    showSpinner,
                    hideSpinner,
                    onSettled: () => document.dispatchEvent(new CustomEvent('splitpal:groups-updated'))
                });
            } else {
                // Eagerly run delta-sync in the background on page load/visit
                (async () => {
                    try {
                        const { syncGroups } = await import('./api.mjs');
                        await syncGroups();
                    } catch (err) {
                        console.error('Auto-sync on page load failed:', err);
                    } finally {
                        // Fired even on failure so the recent-groups widget doesn't spin forever.
                        document.dispatchEvent(new CustomEvent('splitpal:groups-updated'));
                    }
                })();
            }
            return;
        }

        showLoggedOutUI();
        removeLocally(CACHED_EMAIL_KEY);
        removeLocally('splitpal_cached_account_label');

        // Only a genuine logout, not the "needs verification" limbo state
        // (still the same account, just pending confirmation) or the very
        // first onAuthStateChanged firing for a visitor who was never logged
        // in at all (also reports user === null, but isn't a logout).
        if (!user && isSettledTransition) {
            // Clears the account's groups but keeps never-claimed guest
            // groups (same rule as main.mjs's logout handler).
            const { clearAccountGroupsFromStorage } = await import('./api.mjs');
            clearAccountGroupsFromStorage();
            document.dispatchEvent(new CustomEvent('splitpal:groups-updated'));
        }

        if (needsVerification(user)) {
            showVerifyBanner(user.email);
            if (!stopVerificationPoll) {
                const { reloadCurrentUser } = await import('./firebase.mjs');
                stopVerificationPoll = pollUntilVerified(reloadCurrentUser, (verifiedUser) => handleAuthUser(verifiedUser, true));
            }
        } else {
            hideVerifyBanner();
            if (stopVerificationPoll) {
                stopVerificationPoll();
                stopVerificationPoll = null;
            }
        }
    };

    // Resolves once Firebase has actually determined the current auth state
    // (the first onAuthStateChanged firing), not merely once the listener is
    // registered - `auth.currentUser` reads as null until then even for an
    // already-signed-in visitor, since restoring the persisted session is
    // itself async. Callers that branch on `auth.currentUser` (handleLoginClick
    // below) would otherwise race it, e.g. wrongly opening the login modal
    // instead of signing out when clicked shortly after a page load.
    let authPromise = null;
    const ensureAuthSubscription = () => {
        if (!authPromise) {
            authPromise = import('./firebase.mjs').then(({ auth, onAuthStateChanged }) => {
                return new Promise((resolve) => {
                    let firstFire = true;
                    watchAuthState(auth, onAuthStateChanged, async (user, isSettledTransition) => {
                        await handleAuthUser(user, isSettledTransition);
                        if (firstFire) {
                            firstFire = false;
                            resolve(auth);
                        }
                    });
                });
            });
        }
        return authPromise;
    };

    const handleLoginClick = async (e) => {
        e.preventDefault();
        const auth = await ensureAuthSubscription();
        if (auth.currentUser) {
            const { signOut } = await import('./firebase.mjs');
            await signOut();
        } else {
            openLoginModal();
        }
    };

    const handleAvatarClick = (e) => {
        e.preventDefault();
        if (hamburger && mobileMenu) {
            hamburger.classList.toggle('active');
            mobileMenu.classList.toggle('active');
        }
    };

    loginEls.forEach((el) => el.addEventListener('click', handleLoginClick));
    if (avatarEl) avatarEl.addEventListener('click', handleAvatarClick);

    // The cached-email marker lives in localStorage, but the Firebase session
    // itself lives in IndexedDB - so a visitor who cleared localStorage (or
    // lost it any other way) can hold a fully live session with no marker.
    // Without this check the header claims "Login" while every API call still
    // authenticates as them (reproduced 2026-07-19: "guest" tab creation
    // landed in the ghost session's account). Peeking at the session record
    // costs a few ms and no bundle bytes; Firebase still only loads for
    // visitors who actually have a session to reconcile.
    // (indexedDB.open would CREATE the db as a side effect, confusing the SDK
    // later - hence the databases() existence check first, and old browsers
    // without it just keep the marker-only behavior.)
    const hasPersistedFirebaseSession = async () => {
        try {
            if (!indexedDB.databases) return false;
            const dbs = await indexedDB.databases();
            if (!dbs.some((d) => d.name === 'firebaseLocalStorageDb')) return false;
            return await new Promise((resolve) => {
                const open = indexedDB.open('firebaseLocalStorageDb');
                open.onerror = () => resolve(false);
                open.onsuccess = () => {
                    const db = open.result;
                    try {
                        const keysReq = db.transaction('firebaseLocalStorage', 'readonly')
                            .objectStore('firebaseLocalStorage').getAllKeys();
                        keysReq.onsuccess = () => {
                            db.close();
                            resolve(keysReq.result.some((k) => String(k).startsWith('firebase:authUser:')));
                        };
                        keysReq.onerror = () => { db.close(); resolve(false); };
                    } catch (e) {
                        db.close();
                        resolve(false);
                    }
                };
            });
        } catch (e) {
            return false;
        }
    };

    // Optimistic display for a returning, previously-logged-in-here visitor —
    // shows the avatar immediately instead of flashing the logged-out hamburger
    // while Firebase loads and confirms the real state.
    const cachedEmail = getLocally(CACHED_EMAIL_KEY);
    if (cachedEmail && avatarEl) {
        applyAvatar(cachedEmail, false, null);
        document.body.classList.add('logged-in');
        if (hamburger) hamburger.style.display = '';
        loginEls.forEach((el) => {
            (el.closest('li') || el).style.display = '';
            el.textContent = 'Logout';
        });
        storeLocally('splitpal_cached_account_label', cachedEmail);
        ensureAuthSubscription();
    } else {
        hasPersistedFirebaseSession().then((has) => {
            if (has) ensureAuthSubscription();
        });
    }
});
